'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import * as React from 'react';
import { styled } from '@mui/material/styles';
import { useMergedRefs } from '@base-ui/utils/useMergedRefs';
import { useStore } from '@base-ui/utils/store';
import CheckIcon from '@mui/icons-material/Check';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import ListSubheader from '@mui/material/ListSubheader';
import { useEventCalendarStoreContext } from '@mui/x-scheduler-internals/use-event-calendar-store-context';
import { eventCalendarPreferenceSelectors, eventCalendarViewSelectors } from '@mui/x-scheduler-internals/event-calendar-selectors';
import clsx from 'clsx';
import { useEventCalendarStyledContext } from "../../EventCalendarStyledContext.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const PreferencesMenuRoot = styled('div', {
  name: 'MuiEventCalendar',
  slot: 'PreferencesMenu'
})({});
const PreferencesMenuListItemIcon = styled(ListItemIcon, {
  name: 'MuiEventCalendar',
  slot: 'PreferencesMenuListItemIcon'
})({
  justifyContent: 'flex-end',
  '&[data-checked="false"]': {
    visibility: 'hidden'
  }
});
const PreferencesMenuListSubheader = styled(ListSubheader, {
  name: 'MuiEventCalendar',
  slot: 'PreferencesMenuListSubheader'
})(({
  theme
}) => _extends({}, theme.typography.button, {
  color: (theme.vars || theme).palette.text.disabled,
  paddingBottom: theme.spacing(1),
  backgroundColor: 'transparent'
}));
export const PreferencesMenu = /*#__PURE__*/React.forwardRef(function PreferencesMenu(props, forwardedRef) {
  // Context hooks
  const {
    schedulerId,
    classes,
    localeText
  } = useEventCalendarStyledContext();
  const store = useEventCalendarStoreContext();

  // Ref hooks
  const containerRef = React.useRef(null);
  const handleRef = useMergedRefs(forwardedRef, containerRef);

  // Selector hooks
  const currentView = useStore(store, eventCalendarViewSelectors.view);
  const preferences = useStore(store, eventCalendarPreferenceSelectors.all);
  const preferencesMenuConfig = useStore(store, eventCalendarPreferenceSelectors.menuConfig);

  // State hooks (must come before any early returns)
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleToggle = (key, checked, event) => {
    store.setPreferences({
      [key]: checked
    }, event);
  };
  const handleTimeFormatChange = (value, event) => {
    store.setPreferences({
      ampm: value === '12'
    }, event);
  };
  const handleWeekStartsOnChange = (value, event) => {
    store.setPreferences({
      weekStartsOn: value
    }, event);
  };
  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Early return if config is false
  if (preferencesMenuConfig === false) {
    return null;
  }
  const preferenceOptions = [{
    configKey: 'toggleWeekendVisibility',
    preferenceKey: 'showWeekends',
    label: localeText.showWeekends
  }, {
    configKey: 'toggleWeekNumberVisibility',
    preferenceKey: 'showWeekNumber',
    label: localeText.showWeekNumber
  }];
  const viewSpecificPreferenceOptions = [{
    configKey: 'toggleEmptyDaysInAgenda',
    preferenceKey: 'showEmptyDaysInAgenda',
    view: 'agenda',
    label: localeText.showEmptyDaysInAgenda
  }];
  const visibleOptions = preferenceOptions.filter(option => preferencesMenuConfig?.[option.configKey] !== false);
  const visibleViewSpecificOptions = viewSpecificPreferenceOptions.filter(option => preferencesMenuConfig?.[option.configKey] !== false && option.view === currentView);
  const showSpecificOptions = visibleViewSpecificOptions.length > 0;
  const showTimeFormatSubmenu = preferencesMenuConfig?.toggleAmpm !== false;
  const showWeekStartsOnSubmenu = preferencesMenuConfig?.toggleWeekStartsOn !== false;
  const showDividerBeforeTimeFormat = showTimeFormatSubmenu && visibleOptions.length > 0;
  const showDividerBeforeWeekStartsOn = showWeekStartsOnSubmenu && (visibleOptions.length > 0 || showTimeFormatSubmenu);
  const showDividerBeforeSpecificOptions = showSpecificOptions && (visibleOptions.length > 0 || showTimeFormatSubmenu || showWeekStartsOnSubmenu);

  // Early return if no menu items to show
  if (!showTimeFormatSubmenu && !showWeekStartsOnSubmenu && visibleOptions.length === 0 && !showSpecificOptions) {
    return null;
  }
  return /*#__PURE__*/_jsxs(PreferencesMenuRoot, _extends({
    ref: handleRef
  }, props, {
    className: clsx(props.className, classes.preferencesMenu),
    children: [/*#__PURE__*/_jsx(IconButton, {
      className: classes.preferencesMenuButton,
      "aria-label": localeText.preferencesMenu,
      onClick: handleClick,
      "aria-controls": open ? `${schedulerId}-preferences-menu` : undefined,
      "aria-haspopup": "true",
      "aria-expanded": open ? 'true' : undefined,
      children: /*#__PURE__*/_jsx(SettingsOutlined, {})
    }), /*#__PURE__*/_jsxs(Menu, {
      className: classes.preferencesMenuList,
      id: `${schedulerId}-preferences-menu`,
      anchorEl: anchorEl,
      open: open,
      onClose: handleClose,
      container: containerRef.current,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right'
      },
      transformOrigin: {
        vertical: 'top',
        horizontal: 'right'
      },
      slotProps: {
        list: {
          'aria-label': localeText.preferencesMenu
        },
        paper: {
          sx: {
            minWidth: 220
          }
        }
      },
      children: [visibleOptions.map(option => /*#__PURE__*/_jsxs(MenuItem, {
        className: classes.preferencesMenuItem,
        role: "menuitemcheckbox",
        "aria-checked": !!preferences[option.preferenceKey],
        onClick: event => {
          handleToggle(option.preferenceKey, !preferences[option.preferenceKey], event.nativeEvent);
        },
        children: [/*#__PURE__*/_jsx(ListItemText, {
          className: classes.preferencesMenuListItemText,
          children: option.label
        }), /*#__PURE__*/_jsx(PreferencesMenuListItemIcon, {
          className: classes.preferencesMenuListItemIcon,
          "data-checked": !!preferences[option.preferenceKey],
          children: /*#__PURE__*/_jsx(CheckIcon, {
            fontSize: "small"
          })
        })]
      }, option.configKey)), showDividerBeforeTimeFormat && /*#__PURE__*/_jsx(Divider, {
        className: classes.preferencesMenuDivider
      }), showTimeFormatSubmenu && /*#__PURE__*/_jsx(PreferencesMenuListSubheader, {
        className: classes.preferencesMenuListSubheader,
        children: localeText.timeFormat
      }), showTimeFormatSubmenu && /*#__PURE__*/_jsxs(MenuItem, {
        className: classes.preferencesMenuItem,
        role: "menuitemradio",
        "aria-checked": !!preferences.ampm,
        onClick: event => {
          handleTimeFormatChange('12', event.nativeEvent);
        },
        children: [/*#__PURE__*/_jsx(ListItemText, {
          className: classes.preferencesMenuListItemText,
          children: localeText.amPm12h
        }), /*#__PURE__*/_jsx(PreferencesMenuListItemIcon, {
          className: classes.preferencesMenuListItemIcon,
          "data-checked": !!preferences.ampm,
          children: /*#__PURE__*/_jsx(CheckIcon, {
            fontSize: "small"
          })
        })]
      }), showTimeFormatSubmenu && /*#__PURE__*/_jsxs(MenuItem, {
        className: classes.preferencesMenuItem,
        role: "menuitemradio",
        "aria-checked": !preferences.ampm,
        onClick: event => {
          handleTimeFormatChange('24', event.nativeEvent);
        },
        children: [/*#__PURE__*/_jsx(ListItemText, {
          className: classes.preferencesMenuListItemText,
          children: localeText.hour24h
        }), /*#__PURE__*/_jsx(PreferencesMenuListItemIcon, {
          className: classes.preferencesMenuListItemIcon,
          "data-checked": !preferences.ampm,
          children: /*#__PURE__*/_jsx(CheckIcon, {
            fontSize: "small"
          })
        })]
      }), showDividerBeforeWeekStartsOn && /*#__PURE__*/_jsx(Divider, {
        className: classes.preferencesMenuDivider
      }), showWeekStartsOnSubmenu && /*#__PURE__*/_jsx(PreferencesMenuListSubheader, {
        className: classes.preferencesMenuListSubheader,
        children: localeText.startWeekOn
      }), showWeekStartsOnSubmenu && [{
        value: 0,
        label: localeText.weekdaySunday
      }, {
        value: 1,
        label: localeText.weekdayMonday
      }, {
        value: 6,
        label: localeText.weekdaySaturday
      }].map(({
        value,
        label
      }) => /*#__PURE__*/_jsxs(MenuItem, {
        className: classes.preferencesMenuItem,
        role: "menuitemradio",
        "aria-checked": preferences.weekStartsOn === value,
        onClick: event => {
          handleWeekStartsOnChange(value, event.nativeEvent);
        },
        children: [/*#__PURE__*/_jsx(ListItemText, {
          className: classes.preferencesMenuListItemText,
          children: label
        }), /*#__PURE__*/_jsx(PreferencesMenuListItemIcon, {
          className: classes.preferencesMenuListItemIcon,
          "data-checked": preferences.weekStartsOn === value,
          children: /*#__PURE__*/_jsx(CheckIcon, {
            fontSize: "small"
          })
        })]
      }, value)), showDividerBeforeSpecificOptions && /*#__PURE__*/_jsx(Divider, {
        className: classes.preferencesMenuDivider
      }), showSpecificOptions && /*#__PURE__*/_jsx(PreferencesMenuListSubheader, {
        className: classes.preferencesMenuListSubheader,
        children: localeText.viewSpecificOptions(currentView)
      }), showSpecificOptions && visibleViewSpecificOptions.map(option => /*#__PURE__*/_jsxs(MenuItem, {
        className: classes.preferencesMenuItem,
        role: "menuitemcheckbox",
        "aria-checked": !!preferences[option.preferenceKey],
        onClick: event => {
          handleToggle(option.preferenceKey, !preferences[option.preferenceKey], event.nativeEvent);
        },
        children: [/*#__PURE__*/_jsx(ListItemText, {
          className: classes.preferencesMenuListItemText,
          children: option.label
        }), /*#__PURE__*/_jsx(PreferencesMenuListItemIcon, {
          className: classes.preferencesMenuListItemIcon,
          "data-checked": !!preferences[option.preferenceKey],
          children: /*#__PURE__*/_jsx(CheckIcon, {
            fontSize: "small"
          })
        })]
      }, option.configKey))]
    })]
  }));
});
if (process.env.NODE_ENV !== "production") PreferencesMenu.displayName = "PreferencesMenu";